// Browser-compatible encryption using Web Crypto API
const ENCRYPTION_KEY = import.meta.env.VITE_ENCRYPTION_KEY || 'idecide-secure-key-2024';

// Convert string to ArrayBuffer
function stringToArrayBuffer(str: string): ArrayBuffer {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

// Convert ArrayBuffer to string
function arrayBufferToString(buffer: ArrayBuffer): string {
  const decoder = new TextDecoder();
  return decoder.decode(buffer);
}

// Convert string to base64
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Convert base64 to ArrayBuffer
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

// Generate a key from the password
async function generateKey(password: string): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );
  
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('idecide-salt'),
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

export async function encryptData(data: string): Promise<string> {
  try {
    if (!data || data.trim() === '') {
      return '';
    }
    
    const key = await generateKey(ENCRYPTION_KEY);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encodedData = stringToArrayBuffer(data);
    
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encodedData
    );
    
    // Combine IV and encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);
    
    return arrayBufferToBase64(combined.buffer);
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data. Please try again.');
  }
}

export async function decryptData(encryptedData: string): Promise<string> {
  try {
    if (!encryptedData || encryptedData.trim() === '') {
      return '';
    }
    
    const key = await generateKey(ENCRYPTION_KEY);
    const combined = base64ToArrayBuffer(encryptedData);
    const combinedArray = new Uint8Array(combined);
    
    // Extract IV and encrypted data
    const iv = combinedArray.slice(0, 12);
    const encrypted = combinedArray.slice(12);
    
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      encrypted
    );
    
    return arrayBufferToString(decrypted);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Failed to decrypt data. The data may be corrupted.');
  }
}

// Helper function to check if data is encrypted
export function isEncrypted(data: string): boolean {
  try {
    if (!data || data.trim() === '') {
      return false;
    }
    
    // Check if it's base64 encoded (our encryption format)
    const decoded = atob(data);
    const array = new Uint8Array(decoded.length);
    for (let i = 0; i < decoded.length; i++) {
      array[i] = decoded.charCodeAt(i);
    }
    
    // Our encrypted data should be at least 12 bytes (IV) + some encrypted content
    return array.length >= 13;
  } catch {
    return false;
  }
}